import * as React from 'react'
import { createPortal } from 'react-dom'

// Event-history timeline of a submission, read from log/specialized_logs through
// SubmissionsController#event_history. Two faces share the same timeline:
//
// * EventHistory (default export): the floating window, mounted through remount as
//   <event-history-component props-json='{"submissionId":..,"url":..,"open":..}'> inside
//   #event-history-btn on the analyte page (analytes/_side_panel). It renders the
//   status-bar button; the window itself is portalled to document.body so it floats above
//   the page and stays open until its close button. Its footer offers "Replicate on a new
//   submission", which opens the new-submission page with ?replicate_from=<id>.
// * EventHistoryPanel: the same timeline as an inline side panel on the new-submission
//   page (SubmissionZip), where clicking an event picks the time tag up to which the
//   history is replicated onto the new submission (HistoryReplayJob).
//
// * While open, the current URL and every link to an analyte page carry
//   ?event_history=open, so navigating to another analyte (or reloading) re-opens it:
//   AnalytesController#show reads that param and passes open: true back in.
// * The history is fetched from SubmissionsController#event_history only after the page
//   has finished loading (idle callback), so it never delays the chromatogram requests.
// * Window position/size and the timeline order are remembered for the browser session.

const PARAM = 'event_history'
const OPEN_VALUE = 'open'
const GEOMETRY_KEY = 'lcAutofit.eventHistoryWindow.geometry'
const ORDER_KEY = 'lcAutofit.eventHistoryWindow.order'
const ANALYTE_LINK = /\/submissions\/\d+\/analytes\/\d+/
const DEFAULT_WIDTH = 460

// Display names per log type. These win over the title the server sends, so wording can be
// tuned here without touching HistoryRunner (e.g. "fileconvert" is shown as "Preparing samples").
const TYPE_META = {
    fileconvert: { icon: 'bi-file-earmark-arrow-down', title: 'Preparing samples' },
    fia: { icon: 'bi-lightning-charge', title: 'FIA profiling' },
    lcpreprocess: { icon: 'bi-gear-wide-connected', title: 'Preprocessing' },
    lcprofileexp: { icon: 'bi-play-circle', title: 'Profiling of experimental samples' },
    applynewrt: { icon: 'bi-sliders', title: 'RT window applied' },
    lccalpointremoval: { icon: 'bi-bullseye', title: 'Calibration points changed' },
}
const ORDER_LABELS = { asc: 'Oldest first', desc: 'Newest first' }
// Jobs every new submission runs on its own: preparing samples, then preprocessing (Panel
// A/B) or the first FIA profiling run (DFI panels, see firstFiaEvent). The replay never
// re-runs them, so on the new-submission page they are always included and cannot be
// chosen as the cutoff. Later FIA runs are re-runs with a new RT window and are replayed.
const ALWAYS_RUN_TYPES = new Set(['fileconvert', 'lcpreprocess'])

function firstFiaEvent(events) {
    return (events || []).find((event) => event.type === 'fia') || null
}

function alwaysRuns(event, events) {
    return ALWAYS_RUN_TYPES.has(event.type) || (event.type === 'fia' && event === firstFiaEvent(events))
}

// ---------------------------------------------------------------- helpers

function readStorage(key) {
    try { return window.sessionStorage.getItem(key) } catch (_) { return null }
}

function writeStorage(key, value) {
    try { window.sessionStorage.setItem(key, value) } catch (_) { /* storage unavailable */ }
}

function paramInUrl() {
    try { return new URLSearchParams(window.location.search).get(PARAM) === OPEN_VALUE } catch (_) { return false }
}

// Keep ?event_history=open on the current URL (so a reload keeps the window) and on
// every analyte link (so the next analyte page opens with it).
function syncParam(open) {
    try {
        const url = new URL(window.location.href)
        if (open) url.searchParams.set(PARAM, OPEN_VALUE)
        else url.searchParams.delete(PARAM)
        if (url.href !== window.location.href) window.history.replaceState(window.history.state, '', url.href)
    } catch (_) { /* ignore */ }
    document.querySelectorAll('a[href]').forEach((anchor) => {
        const href = anchor.getAttribute('href')
        if (!href || !ANALYTE_LINK.test(href)) return
        try {
            const url = new URL(href, window.location.origin)
            if (url.origin !== window.location.origin) return
            if (open) url.searchParams.set(PARAM, OPEN_VALUE)
            else url.searchParams.delete(PARAM)
            anchor.setAttribute('href', url.pathname + url.search + url.hash)
        } catch (_) { /* leave the link alone */ }
    })
}

// The page body is rendered with CSS zoom, so pointer distances (screen pixels) are
// converted to layout pixels before being applied to the window.
function bodyZoom() {
    const value = parseFloat(getComputedStyle(document.body).zoom)
    return Number.isFinite(value) && value > 0 ? value : 1
}

// Keep at least the header reachable inside the viewport.
function clampPosition(left, top, width, height, zoom = bodyZoom()) {
    const viewportWidth = window.innerWidth / zoom
    const viewportHeight = window.innerHeight / zoom
    const minVisible = 80
    return {
        left: Math.min(Math.max(left, minVisible - width), viewportWidth - minVisible),
        top: Math.min(Math.max(top, 0), Math.max(0, viewportHeight - Math.min(height, 48))),
    }
}

function readGeometry() {
    try {
        const geometry = JSON.parse(readStorage(GEOMETRY_KEY) || 'null')
        if (!geometry || typeof geometry !== 'object') return {}
        const result = {}
        if (Number.isFinite(geometry.width) && geometry.width >= 280) result.width = geometry.width
        if (Number.isFinite(geometry.height) && geometry.height >= 200) result.height = geometry.height
        if (Number.isFinite(geometry.left) && Number.isFinite(geometry.top)) {
            const clamped = clampPosition(geometry.left, geometry.top, result.width || DEFAULT_WIDTH, result.height || 400)
            result.left = clamped.left
            result.top = clamped.top
        }
        return result
    } catch (_) {
        return {}
    }
}

function geometryStyle(geometry) {
    const style = {}
    if (geometry.width) style.width = `${geometry.width}px`
    if (geometry.height) style.height = `${geometry.height}px`
    if (Number.isFinite(geometry.left) && Number.isFinite(geometry.top)) {
        style.left = `${geometry.left}px`
        style.top = `${geometry.top}px`
        style.right = 'auto'
        style.bottom = 'auto'
    }
    return style
}

function parseDate(value) {
    if (!value) return null
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
}

// Events without a parseable start time fall back to the unix time tag of their log file.
function eventDate(event) {
    return parseDate(event.started_at) || (event.tag ? new Date(event.tag * 1000) : null)
}

function formatTime(date) {
    if (!date) return '–'
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
}

function formatDay(date) {
    if (!date) return 'Unknown date'
    return date.toLocaleDateString([], { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })
}

function formatDuration(seconds) {
    if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return ''
    const total = Number(seconds)
    if (total < 1) return `${total.toFixed(2)} s`
    if (total < 60) return `${total.toFixed(1)} s`
    const minutes = Math.floor(total / 60)
    const rest = Math.round(total - minutes * 60)
    if (minutes < 60) return `${minutes} m ${rest} s`
    const hours = Math.floor(minutes / 60)
    return `${hours} h ${minutes - hours * 60} m`
}

function outcomeClass(outcome) {
    if (!outcome) return 'is-unknown'
    if (outcome === 'Errored') return 'is-error'
    return 'is-ok'
}

function windowText(window) {
    if (!window) return null
    const { rmin, rmax } = window
    if (rmin === undefined && rmax === undefined) return null
    const unchanged = (value) => value === -1 || value === '-1' || value === null || value === undefined
    if (unchanged(rmin) && unchanged(rmax)) return 'unchanged'
    const num = (value) => (unchanged(value) ? '–' : Number(value).toFixed(3))
    return `${num(rmin)} – ${num(rmax)}`
}

function panelLabelForStatus(status) {
    if (!status) return null
    if (status === 'done') return { className: 'is-ok', text: 'replicated', icon: 'bi-check-circle-fill' }
    if (status === 'failed') return { className: 'is-error', text: 'replication failed', icon: 'bi-exclamation-circle-fill' }
    if (status === 'skipped') return { className: 'is-error', text: 'replication skipped', icon: 'bi-slash-circle' }
    return { className: 'is-pending', text: `replication ${status}`, icon: 'bi-hourglass-split' }
}

function summarize(events, generatedAt, hiddenTypes) {
    const visible = events.filter((event) => !hiddenTypes.has(event.type)).length
    const errored = events.filter((event) => event.outcome === 'Errored').length
    const parts = [`${events.length} event${events.length === 1 ? '' : 's'}`]
    if (visible !== events.length) parts.push(`${visible} shown`)
    if (errored) parts.push(`${errored} errored`)
    const updated = parseDate(generatedAt)
    if (updated) parts.push(`updated ${formatTime(updated)}`)
    return parts.join(' · ')
}

// ---------------------------------------------------------------- timeline pieces

function Chip({ label, value, className = '' }) {
    return (
        <span className={`eh-tag ${className}`}>
            {label}{value !== undefined && value !== null && value !== '' ? <> <b>{value}</b></> : null}
        </span>
    )
}

function OnOffChip({ label, on }) {
    return (
        <span className={`eh-tag ${on ? 'is-on' : 'is-off'}`}>
            <i className={`bi ${on ? 'bi-check-circle-fill' : 'bi-x-circle'}`} /> {label}
        </span>
    )
}

function CalPoints({ points }) {
    return (
        <span className="eh-tag eh-cal-wrap">
            Cal points
            <span className="eh-cal">
                {points.map((point) => (
                    <span key={point.point} className={`pt ${point.selected ? '' : 'is-off'}`}
                        title={`Cal ${point.point} ${point.selected ? 'kept' : 'removed'}`}>
                        {point.point}
                    </span>
                ))}
            </span>
        </span>
    )
}

function ParamChips({ event }) {
    const params = event.params
    if (!params) return null
    if (params.error) {
        return <div className="eh-error"><i className="bi bi-exclamation-triangle" /> {params.error}</div>
    }

    const chips = []
    if (event.type === 'applynewrt') {
        const windows = params.windows || {}
        const analyteWindow = windowText(windows.analyte)
        const istdWindow = windowText(windows.istd)
        if (analyteWindow) chips.push(<Chip key="analyte" label="Analyte RT" value={analyteWindow} />)
        if (istdWindow) chips.push(<Chip key="istd" label="ISTD RT" value={istdWindow} />)
        chips.push(<Chip key="scope" label={params.apply_to_all_samples ? 'All samples' : 'Single sample'} className={params.apply_to_all_samples ? 'is-on' : ''} />)
        chips.push(<OnOffChip key="auto-analyte" label="Auto-adjust analyte" on={!!params.auto_adjust_analyte} />)
        chips.push(<OnOffChip key="auto-istd" label="Auto-adjust ISTD" on={!!params.auto_adjust_istd} />)
        if (params.integration_mode && params.integration_mode !== 'peak_picking') {
            chips.push(<Chip key="mode" label="Mode" value={String(params.integration_mode).replace(/_/g, ' ')} />)
        }
    } else if (event.type === 'fia') {
        if (params.rtmin !== null && params.rtmin !== undefined && params.rtmax !== null && params.rtmax !== undefined) {
            chips.push(<Chip key="window" label="RT window" value={`${Number(params.rtmin).toFixed(2)} – ${Number(params.rtmax).toFixed(2)} min`}
                className={params.window_source === 'submission' ? 'is-off' : ''} />)
            if (params.window_source === 'submission') chips.push(<Chip key="source" label="window not logged; current window of the submission" />)
        }
    } else if (event.type === 'lccalpointremoval') {
        const points = Array.isArray(params.cal_points) ? params.cal_points : []
        if (points.length) chips.push(<CalPoints key="cal" points={points} />)
        else if (params.cal_selected) chips.push(<Chip key="cal" label="Cal points" value={params.cal_selected} />)
        chips.push(<Chip key="scope" label={params.apply_to_all ? 'All analytes' : `${params.analyte_name || 'This analyte'} only`}
            className={params.apply_to_all ? 'is-on' : ''} />)
    }
    return chips.length ? <div className="eh-details">{chips}</div> : null
}

function TimelineItem({ event, selectable = false, selectionState = null, onSelect = null }) {
    const meta = TYPE_META[event.type] || { icon: 'bi-circle', title: event.title || event.type }
    // Cal point logs carry the analyte only in their parameter line, not in the file name.
    const analyteName = event.analyte || event.params?.analyte_name || null
    const date = eventDate(event)
    const duration = formatDuration(event.duration_seconds)
    const outcome = outcomeClass(event.outcome)
    const dotIcon = outcome === 'is-error' ? 'bi-exclamation' : outcome === 'is-ok' ? 'bi-check' : 'bi-dash'
    const classes = ['eh-item']
    if (selectionState === 'always') classes.push('is-always')
    else if (selectable) classes.push('is-selectable')
    if (selectionState === 'cutoff') classes.push('is-cutoff')
    if (selectionState === 'excluded') classes.push('is-excluded')
    const handleSelect = selectable && selectionState !== 'always' && onSelect ? () => onSelect(event.tag) : undefined
    const handleKey = handleSelect ? (keyEvent) => { if (keyEvent.key === 'Enter' || keyEvent.key === ' ') { keyEvent.preventDefault(); handleSelect() } } : undefined
    return (
        <li className={classes.join(' ')} data-type={event.type} data-tag={event.tag}
            onClick={handleSelect} onKeyDown={handleKey} tabIndex={handleSelect ? 0 : undefined}
            role={handleSelect ? 'button' : undefined}
            aria-pressed={handleSelect ? selectionState === 'cutoff' : undefined}
            title={selectionState === 'always'
                ? 'This step always runs on a new submission and cannot be left out'
                : handleSelect ? (selectionState === 'cutoff' ? 'Click again to replicate the whole history' : 'Replicate the history up to this event') : undefined}>
            <div className="eh-time" title={event.log_file || ''}>
                {formatTime(date)}
                {duration ? <span className="eh-duration" title="Run time">{duration}</span> : null}
            </div>
            <div className="eh-rail"><span className={`eh-dot ${outcome}`}><i className={`bi ${dotIcon}`} /></span></div>
            <div className="eh-card">
                <div className="eh-card-head">
                    <span className="eh-type"><i className={`bi ${meta.icon}`} />{meta.title}</span>
                    {analyteName ? (
                        event.analyte_url
                            ? <a className="eh-analyte" href={event.analyte_url} title={`Open ${analyteName}`}>{analyteName}</a>
                            : <span className="eh-analyte is-static">{analyteName}</span>
                    ) : null}
                    <span className={`eh-outcome ${outcome}`} title="Status recorded in the log">
                        {event.outcome || 'No completion logged'}
                    </span>
                </div>
                <ParamChips event={event} />
                {event.error_message ? (
                    <div className="eh-error"><i className="bi bi-exclamation-triangle" /> {event.error_message}</div>
                ) : null}
                {selectionState === 'cutoff' ? (
                    <div className="eh-cutoff-label"><i className="bi bi-flag-fill" /> Replicate up to here</div>
                ) : null}
                {selectionState === 'always' ? (
                    <div className="eh-always-label"><i className="bi bi-lock-fill" /> Always runs on a new submission</div>
                ) : null}
            </div>
        </li>
    )
}

// Chronological list grouped by day. `order` is "asc" (oldest first) or "desc". With
// `selectable`, clicking an event picks its time tag as the replication cutoff: events
// sharing that tag are included, later ones are dimmed as excluded.
function Timeline({ events, hiddenTypes, order, selectable = false, selectedTag = null, onSelect = null }) {
    const visible = events.filter((event) => !hiddenTypes.has(event.type))
    if (!events.length) {
        return <div className="event-history-placeholder"><i className="bi bi-inbox" /> No events have been logged for this submission yet.</div>
    }
    if (!visible.length) {
        return <div className="event-history-placeholder">All event types are hidden. Click a type above to show it again.</div>
    }
    const sorted = order === 'desc' ? [...visible].reverse() : visible
    const rows = []
    let lastDay = null
    sorted.forEach((event) => {
        const date = eventDate(event)
        const day = date ? date.toDateString() : 'unknown'
        if (day !== lastDay) {
            rows.push(<li key={`day-${day}-${event.tag}`} className="eh-day">{formatDay(date)}</li>)
            lastDay = day
        }
        let selectionState = null
        if (selectable && alwaysRuns(event, events)) {
            selectionState = 'always'
        } else if (selectable && selectedTag !== null && selectedTag !== undefined) {
            if (event.tag === selectedTag) selectionState = 'cutoff'
            else if (event.tag > selectedTag) selectionState = 'excluded'
        }
        rows.push(<TimelineItem key={`${event.log_file || event.tag}-${event.type}-${event.analyte || ''}`} event={event}
            selectable={selectable} selectionState={selectionState} onSelect={onSelect} />)
    })
    return <ol className="eh-timeline">{rows}</ol>
}

function Toolbar({ events, hiddenTypes, order, onToggleType, onToggleOrder }) {
    const counts = new Map()
    events.forEach((event) => counts.set(event.type, (counts.get(event.type) || 0) + 1))
    return (
        <div className="event-history-toolbar">
            {[...counts.entries()].map(([type, count]) => {
                const meta = TYPE_META[type] || { icon: 'bi-circle', title: events.find((event) => event.type === type)?.title || type }
                const off = hiddenTypes.has(type)
                return (
                    <button key={type} type="button" className={`event-history-chip ${off ? 'is-off' : ''}`}
                        aria-pressed={!off} title={`${off ? 'Show' : 'Hide'} ${meta.title}`} onClick={() => onToggleType(type)}>
                        <i className={`bi ${meta.icon}`} />{meta.title}<span className="count">{count}</span>
                    </button>
                )
            })}
            <button type="button" className="event-history-chip is-order" title="Change the order of the timeline" onClick={onToggleOrder}>
                <i className={`bi ${order === 'asc' ? 'bi-sort-down-alt' : 'bi-sort-up'}`} />{ORDER_LABELS[order]}
            </button>
        </div>
    )
}

// ---------------------------------------------------------------- shared state

// Fetches the history. With `deferred`, the first load waits for the page's load event
// and an idle callback so the chromatogram requests go first.
function useEventHistory(url, { active = true, deferred = false } = {}) {
    const [events, setEvents] = React.useState(null)
    const [payload, setPayload] = React.useState(null)
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState(null)
    const abortRef = React.useRef(null)

    const load = React.useCallback(async () => {
        if (abortRef.current) abortRef.current.abort()
        const controller = new AbortController()
        abortRef.current = controller
        setLoading(true)
        setError(null)
        try {
            const response = await fetch(url, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                signal: controller.signal,
            })
            let payload = {}
            try { payload = await response.json() } catch (_) { payload = {} }
            if (!response.ok) throw new Error(payload.error || `The server answered with HTTP ${response.status}.`)
            setEvents(Array.isArray(payload.events) ? payload.events : [])
            setPayload(payload)
        } catch (err) {
            if (err.name === 'AbortError') return
            console.error('event history:', err)
            setError(err.message || 'The event history could not be loaded.')
        } finally {
            if (abortRef.current === controller) {
                abortRef.current = null
                setLoading(false)
            }
        }
    }, [url])

    React.useEffect(() => {
        if (!active || events !== null) return undefined
        let cancelled = false
        let idleHandle = null
        const run = () => { if (!cancelled) load() }
        if (!deferred) {
            idleHandle = window.setTimeout(run, 0)
            return () => { cancelled = true; window.clearTimeout(idleHandle) }
        }
        const whenIdle = () => {
            if (cancelled) return
            if (typeof window.requestIdleCallback === 'function') idleHandle = window.requestIdleCallback(run, { timeout: 2000 })
            else idleHandle = window.setTimeout(run, 150)
        }
        if (document.readyState === 'complete') whenIdle()
        else window.addEventListener('load', whenIdle, { once: true })
        return () => {
            cancelled = true
            window.removeEventListener('load', whenIdle)
            if (idleHandle !== null && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleHandle)
        }
    }, [active, deferred, events, load])

    React.useEffect(() => () => { if (abortRef.current) abortRef.current.abort() }, [])

    return { events, payload, loading, error, load }
}

// Toolbar + timeline + summary text shared by the popup and the panel.
function useTimelineView({ events, payload, loading, error, load, selectable = false, selectedTag = null, onSelect = null }) {
    const [hiddenTypes, setHiddenTypes] = React.useState(() => new Set())
    const [order, setOrder] = React.useState(() => (readStorage(ORDER_KEY) === 'desc' ? 'desc' : 'asc'))
    const bodyRef = React.useRef(null)
    const scrolledRef = React.useRef(false)

    // Show the latest event the first time the timeline appears (oldest-first order).
    React.useEffect(() => {
        if (!events || scrolledRef.current || order !== 'asc' || !bodyRef.current) return
        bodyRef.current.scrollTop = bodyRef.current.scrollHeight
        scrolledRef.current = true
    }, [events, order])

    const toggleType = (type) => {
        setHiddenTypes((current) => {
            const next = new Set(current)
            if (next.has(type)) next.delete(type)
            else next.add(type)
            return next
        })
    }

    const toggleOrder = () => {
        setOrder((current) => {
            const next = current === 'asc' ? 'desc' : 'asc'
            writeStorage(ORDER_KEY, next)
            return next
        })
    }

    let body
    if (events === null) {
        body = error
            ? (
                <div className="event-history-placeholder is-error">
                    <i className="bi bi-exclamation-octagon" />
                    <span>{error}</span>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={load}>Try again</button>
                </div>
            )
            : (
                <div className="event-history-placeholder">
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    <span>Loading event history…</span>
                </div>
            )
    } else {
        body = <Timeline events={events} hiddenTypes={hiddenTypes} order={order}
            selectable={selectable} selectedTag={selectedTag} onSelect={onSelect} />
    }

    let summary = 'Times are shown in your local time zone.'
    if (events) summary = summarize(events, payload?.generated_at, hiddenTypes)
    if (events && error) summary = `Could not refresh: ${error}`

    const toolbar = events && events.length > 0
        ? <Toolbar events={events} hiddenTypes={hiddenTypes} order={order} onToggleType={toggleType} onToggleOrder={toggleOrder} />
        : null

    return { body, bodyRef, toolbar, summary }
}

function RefreshButton({ loading, onClick }) {
    return (
        <button type="button" className={`event-history-icon-btn ${loading ? 'is-loading' : ''}`} disabled={loading}
            title="Refresh" aria-label="Refresh event history" onClick={onClick}>
            <i className="bi bi-arrow-clockwise" />
        </button>
    )
}

// ---------------------------------------------------------------- popup (analyte page)

export default function EventHistory(props) {
    const submissionId = props.submissionId
    const url = props.url || `/submissions/${submissionId}/event_history`
    const [open, setOpen] = React.useState(() => !!props.open || paramInUrl())
    const [geometry, setGeometry] = React.useState(readGeometry)
    const [dragging, setDragging] = React.useState(false)
    const [portalNode] = React.useState(() => {
        const node = document.createElement('div')
        node.className = 'event-history-portal'
        // Never let Turbo snapshot the window: it is re-created by this component.
        node.setAttribute('data-turbo-temporary', '')
        return node
    })
    const windowRef = React.useRef(null)
    const history = useEventHistory(url, { active: open, deferred: true })
    const { events, payload, loading, error, load } = history
    const { body, bodyRef, toolbar, summary } = useTimelineView(history)

    // The window floats above the page from document.body.
    React.useEffect(() => {
        document.body.appendChild(portalNode)
        return () => { portalNode.remove() }
    }, [portalNode])

    React.useEffect(() => { syncParam(open) }, [open])

    // Remember the size after the user resizes the window (CSS resize handle).
    React.useEffect(() => {
        const element = windowRef.current
        if (!open || !element || typeof ResizeObserver !== 'function') return undefined
        let timer = null
        const observer = new ResizeObserver(() => {
            window.clearTimeout(timer)
            timer = window.setTimeout(() => {
                setGeometry((current) => {
                    const next = { ...current, width: element.offsetWidth, height: element.offsetHeight }
                    writeStorage(GEOMETRY_KEY, JSON.stringify(next))
                    return next
                })
            }, 200)
        })
        observer.observe(element)
        return () => { observer.disconnect(); window.clearTimeout(timer) }
    }, [open])

    const startDrag = (event) => {
        const element = windowRef.current
        if (!element) return
        if (event.button !== undefined && event.button !== 0) return
        if (event.target.closest('button, a, input, select')) return
        event.preventDefault()
        const zoom = bodyZoom()
        const start = { x: event.clientX, y: event.clientY, left: element.offsetLeft, top: element.offsetTop }
        let last = { left: start.left, top: start.top }
        element.style.left = `${start.left}px`
        element.style.top = `${start.top}px`
        element.style.right = 'auto'
        element.style.bottom = 'auto'
        setDragging(true)

        const onMove = (moveEvent) => {
            if (moveEvent.pointerId !== event.pointerId) return
            const left = start.left + (moveEvent.clientX - start.x) / zoom
            const top = start.top + (moveEvent.clientY - start.y) / zoom
            last = clampPosition(left, top, element.offsetWidth, element.offsetHeight, zoom)
            element.style.left = `${last.left}px`
            element.style.top = `${last.top}px`
        }
        const onEnd = () => {
            window.removeEventListener('pointermove', onMove)
            window.removeEventListener('pointerup', onEnd)
            window.removeEventListener('pointercancel', onEnd)
            setDragging(false)
            setGeometry((current) => {
                const next = { ...current, left: last.left, top: last.top, width: element.offsetWidth, height: element.offsetHeight }
                writeStorage(GEOMETRY_KEY, JSON.stringify(next))
                return next
            })
        }
        window.addEventListener('pointermove', onMove)
        window.addEventListener('pointerup', onEnd)
        window.addEventListener('pointercancel', onEnd)
    }

    const replicationState = panelLabelForStatus(payload?.replication?.status)
    const windowElement = open ? (
        <div ref={windowRef} className={`event-history-window shadow-lg is-open ${dragging ? 'is-dragging' : ''}`}
            role="dialog" aria-label="Event history" style={geometryStyle(geometry)}>
            <div className="event-history-header" onPointerDown={startDrag}>
                <div className="event-history-title">
                    <i className="bi bi-clock-history" />
                    <span>Event history</span>
                    <span className="event-history-subtitle">Submission #{submissionId}</span>
                </div>
                <div className="event-history-actions">
                    <RefreshButton loading={loading} onClick={load} />
                    <button type="button" className="event-history-icon-btn" title="Close" aria-label="Close event history" onClick={() => setOpen(false)}>
                        <i className="bi bi-x-lg" />
                    </button>
                </div>
            </div>
            {toolbar}
            <div className="event-history-body" ref={bodyRef}>{body}</div>
            <div className="event-history-footer">
                <span>{summary}</span>
                <span className="event-history-footer-actions">
                    {replicationState ? (
                        <span className={`event-history-replication-state ${replicationState.className}`}
                            title={payload.replication.message || ''}>
                            <i className={`bi ${replicationState.icon}`} />
                            {`from #${payload.replication.source_submission_id}: ${replicationState.text}`}
                        </span>
                    ) : null}
                    {payload?.replicate_url ? (
                        <a className="event-history-replicate-btn" href={payload.replicate_url}
                            title="Create a new submission for this kit and replay this history onto it">
                            <i className="bi bi-copy" /> Replicate on a new submission
                        </a>
                    ) : null}
                    <span className="event-history-resize-hint" title="Drag the header to move, drag this corner to resize">
                        <i className="bi bi-arrows-angle-expand" />
                    </span>
                </span>
            </div>
        </div>
    ) : null

    return (
        <>
            <button type="button" className={`event-history-btn ${open ? 'is-open' : ''} bg-light border-dark border`} title="Event history"
                aria-label={open ? 'Hide event history' : 'Show event history'} aria-expanded={open}
                onClick={() => setOpen((current) => !current)}>
                <i className="bi bi-clock-history" />
            </button>
            {createPortal(windowElement, portalNode)}
        </>
    )
}

// ---------------------------------------------------------------- panel (new-submission page)

// Inline timeline of the source submission. `selectedTag` (unix time tag or null for the
// whole history) is owned by the parent, which sends it along with the new submission.
export function EventHistoryPanel({ submissionId, url, subtitle = null, selectedTag = null, onSelect = () => {}, onEventsLoaded = null }) {
    const endpoint = url || `/submissions/${submissionId}/event_history`
    const history = useEventHistory(endpoint, { active: true, deferred: false })
    const { events, loading, load } = history
    const handleSelect = (tag) => onSelect(tag === selectedTag ? null : tag)
    const alwaysIncluded = (event) => alwaysRuns(event, events)

    // Lets the parent react to the loaded history (SubmissionZip prefills the DFI RT window from it).
    React.useEffect(() => {
        if (events && typeof onEventsLoaded === 'function') onEventsLoaded(events)
    }, [events, onEventsLoaded])
    const { body, bodyRef, toolbar, summary } = useTimelineView({ ...history, selectable: true, selectedTag, onSelect: handleSelect })

    let selectionText = null
    if (events && events.length) {
        if (selectedTag === null || selectedTag === undefined) {
            selectionText = <span>Replicating the <b>whole history</b> ({events.length} event{events.length === 1 ? '' : 's'}). Click an event to stop after it.</span>
        } else {
            const included = events.filter((event) => alwaysIncluded(event) || event.tag <= selectedTag)
            const cutoff = included.length ? eventDate(included[included.length - 1]) : null
            selectionText = <span>Replicating <b>{included.length} of {events.length}</b> events, up to <b>{cutoff ? `${formatDay(cutoff)} ${formatTime(cutoff)}` : selectedTag}</b>.</span>
        }
    }

    return (
        <div className="event-history-panel" role="region" aria-label="Event history to replicate">
            <div className="event-history-header">
                <div className="event-history-title">
                    <i className="bi bi-clock-history" />
                    <span>Replicate event history</span>
                    <span className="event-history-subtitle">{`from submission #${submissionId}${subtitle ? ` · ${subtitle}` : ''}`}</span>
                </div>
                <div className="event-history-actions">
                    <RefreshButton loading={loading} onClick={load} />
                </div>
            </div>
            {selectionText ? (
                <div className="event-history-selection">
                    {selectionText}
                    {selectedTag !== null && selectedTag !== undefined ? (
                        <button type="button" className="event-history-chip" onClick={() => onSelect(null)} title="Replicate the whole history">
                            <i className="bi bi-arrow-counterclockwise" />Whole history
                        </button>
                    ) : null}
                </div>
            ) : null}
            {toolbar}
            <div className="event-history-body" ref={bodyRef}>{body}</div>
            <div className="event-history-footer">
                <span>{summary}</span>
            </div>
        </div>
    )
}
